import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Role } from 'context/authentication/Role.types'
import { outlineAnimation } from 'global/helpers/animations'
import { hasAccess } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import DocumentLink from 'screens/StatusManager/DocumentLink'
import DocumentsUploadDialog from 'screens/StatusManager/DocumentsUploadDialog'
import StatusUpdateWithAttachmentsDialog from 'screens/StatusManager/StatusUpdateWithAttachmentsDialog'
import LocationLink from 'screens/TripDashboard/LocationLink'
import PrimaryActions from 'screens/TripDetails/PrimaryActions'
import StopHeader from 'screens/TripDetails/StopHeader'
import type { KNDataCardAction } from 'screens/TripDetails/TripDetails.types'
import type { LegCardPrimaryStopProps } from './LegCard.types'

const LegCardPrimaryStop = ({
  trip,
  leg,
  stop,
  weblinkToken,
  collapsed = false,
}: LegCardPrimaryStopProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation<{ wayPointCids: string[] }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const statusUpdateWithAttachmentsDialog = useDialog<typeof StatusUpdateWithAttachmentsDialog>()
  const documentsUploadDialog = useDialog<typeof DocumentsUploadDialog>()
  const [isHighlighted, setIsHighlighted] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const actions: KNDataCardAction[] = []
  if (stop.availableStatuses.length > 0) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.update_status_and_documents'),
      handler: statusUpdateWithAttachmentsDialog.handleOpen,
    })
  }
  if (stop.statuses.length > 0 && stop.availableStatuses.length === 0) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.upload_documents'),
      handler: documentsUploadDialog.handleOpen,
      variant: 'outlined',
    })
  }

  // NOTE: make whole card clickable, but only for weblink and only on mobile
  const handleCardClick = (): void => {
    if (!weblinkToken || !isMobile || stop.availableStatuses.length === 0) {
      return
    }
    statusUpdateWithAttachmentsDialog.handleOpen()
  }

  const getBackgroundColor = (): string | undefined => {
    switch (stop.type) {
      case 'PUP':
        return '#ecf2fa'
      case 'DEL':
        return '#d3ddf1'
      default:
        return undefined
    }
  }

  useEffect(() => {
    setIsHighlighted(location.state?.wayPointCids.includes(stop.wayPointCid))
  }, [location.state])

  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView()
    }
  }, [isHighlighted])

  return (
    <>
      <Box
        data-guide={`${stop.type}-card`}
        ref={cardRef}
        sx={{
          width: '100%',
          padding: { xs: 2, md: 0 },
          background: { xs: getBackgroundColor(), md: 'none' },
          animation: isHighlighted ? outlineAnimation() : undefined,
        }}
        onClick={handleCardClick}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{
            height: { sx: 'auto', md: '100%' },
          }}
        >
          <StopHeader leg={leg} stop={stop} />
          <Collapse in={!collapsed} timeout={200} sx={{ flexGrow: 1 }}>
            <Stack spacing={1}>
              {isMobile && stop.type === 'PUP' && leg.collectionInstructions && (
                <Box>
                  <Typography variant="p6" component="p" color="primary.light">
                    {t('screens.cs.trip_details.shipment_details.collection_instructions')}
                  </Typography>
                  <Typography variant="p3" color="primary.main">
                    {leg.collectionInstructions}
                  </Typography>
                </Box>
              )}
              {isMobile && stop.type === 'DEL' && leg.deliveryInstructions && (
                <Box>
                  <Typography variant="p6" component="p" color="primary.light">
                    {t('screens.cs.trip_details.shipment_details.delivery_instructions')}
                  </Typography>
                  <Typography variant="p3" color="primary.main">
                    {leg.deliveryInstructions}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="p4" color="primary.main">
                  {stop.address.name.join(' ')}
                </Typography>
                <LocationLink
                  countryCode={stop.address.countryCode}
                  city={stop.address.city.join(', ')}
                  zipCode={stop.address.zipCode}
                  street={stop.address.street.join(', ')}
                  marker={stop.geoPoint ? {
                    id: stop.wayPointCid,
                    latitude: stop.geoPoint.latitude,
                    longitude: stop.geoPoint.longitude,
                    type: stop.type,
                  } : undefined}
                  lineBreaks="street"
                />
              </Box>
              {stop.documents && (
                <Box>
                  <Typography variant="p6" component="p" color="primary.main">
                    {t('screens.cs.trip_details.card.attachments')}
                  </Typography>
                  <Typography variant="p3" color="primary.main">
                    {stop.documents
                      .filter((file) => ['POP', 'POD'].includes(file.fileType))
                      .map((file, index) => (
                        <DocumentLink
                          key={file.id}
                          file={file}
                          index={index > 1 ? index : undefined}
                          weblinkToken={weblinkToken}
                        />
                      ))}
                    {stop.documents
                      .filter((file) => !['POP', 'POD'].includes(file.fileType))
                      .map((file) => (
                        <DocumentLink key={file.id} file={file} weblinkToken={weblinkToken} />
                      ))}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Collapse>
          {actions.length > 0 && (hasAccess(Role.Editor) || weblinkToken) && (
            <PrimaryActions actions={actions} />
          )}
        </Stack>
      </Box>

      <StatusUpdateWithAttachmentsDialog
        payload={{
          trip,
          leg,
          stop,
          weblinkToken,
        }}
        open={statusUpdateWithAttachmentsDialog.isOpen}
        onAction={statusUpdateWithAttachmentsDialog.handleAction}
        onClose={statusUpdateWithAttachmentsDialog.handleClose}
      />

      <DocumentsUploadDialog
        payload={{
          trip,
          leg,
          stop,
          weblinkToken,
        }}
        open={documentsUploadDialog.isOpen}
        onAction={documentsUploadDialog.handleAction}
        onClose={documentsUploadDialog.handleClose}
      />
    </>
  )
}

export default LegCardPrimaryStop
